//@ts-check
import React, { createContext, useContext, useEffect, useState } from 'react'
import { authorization } from '../../Module/Auth/Auth'
import { CONFIG } from '../../Config/config'
import { mapValues } from 'lodash'
import { usePostHog } from 'posthog-js/react'

const defaultFlags = {
  campaignPipelineV2: parseBoolean(
    process.env.REACT_APP_FEATURE_CAMPAIGN_PIPELINE_V2,
    false,
  ),
  isAdmEnabled: parseBoolean(process.env.REACT_APP_FEATURE_ADM, true),
  autocapture: parseBoolean(process.env.REACT_APP_FEATURE_AUTOCAPTURE, true),
  isBrazeEnabled: parseBoolean(process.env.REACT_APP_FEATURE_BRAZE, true),
  isFigmaEnabled: parseBoolean(process.env.REACT_APP_FEATURE_FIGMA, false),
  isSftpEnabled: false,
}

/**
 * Appends the REACT_APP_NODE_ENV_SUFFIX to a PostHog flag base name.
 * Returns null when no suffix is configured (e.g. local dev), which causes
 * the flag to fall back to its default value.
 * Use this for any flag whose PostHog key is environment-scoped.
 * @param {string} baseName - e.g. 'sftp_enabled'
 * @returns {string | null}
 */
function withEnvSuffix(baseName) {
  const suffix = process.env.REACT_APP_NODE_ENV_SUFFIX
  if (!suffix) return null
  return `${baseName}_${suffix}`
}

const posthogFlagsMap = {
  campaignPipelineV2: 'feature_campaign_pipeline_v2',
  isAdmEnabled: 'is_adm_feature_enabled',
  autocapture: 'is_autocapture_enabled_for_internal_users',
  isBrazeEnabled: 'is_braze_enabled',
  isFigmaEnabled: 'is_figma_integration_enabled',
  isSftpEnabled: withEnvSuffix('sftp_enabled'),
}

export const FeatureFlagsContext = createContext(defaultFlags)

export const FeatureFlagsContextProvider = ({ children }) => {
  const posthog = usePostHog()
  const [featureFlags, setFeatureFlags] = useState(defaultFlags)

  function evaluateFeatureFlags() {
    const currentFlags = mapValues(featureFlags, (fallback, key) => {
      const posthogKey =
        posthogFlagsMap[/** @type {keyof typeof posthogFlagsMap} */ (key)]
      if (!posthogKey) return fallback
      const remote = posthog.isFeatureEnabled(posthogKey)
      if (remote === undefined || remote === null) return fallback
      return remote
    })

    setFeatureFlags(currentFlags)
  }

  useEffect(() => {
    if (posthog) {
      // first load
      evaluateFeatureFlags()
      // set up callback for future flag updates
      posthog.onFeatureFlags(() => {
        evaluateFeatureFlags()
      })
    } else {
      console.warn('Failed to update feature flags, PostHog is not initialized')
    }
  }, [])

  useEffect(() => {
    try {
      if (!featureFlags.autocapture) {
        const user = authorization.getUser()

        if (
          user.role === CONFIG.USER_ROLE.ADMIN ||
          user.role === CONFIG.USER_ROLE.CITY_MANAGER ||
          user.email.includes('@oppizi.com')
        ) {
          posthog.config.autocapture = false
        }
      }
    } catch (error) {
      console.warn({
        message: 'Error while configuring PostHog',
        error,
      })
    }
  }, [featureFlags.autocapture])

  const isAdminOrSelfServeEnabled =
    authorization.isAdmin() || authorization.isSelfServeEnabled()

  return (
    <FeatureFlagsContext.Provider
      value={{
        ...featureFlags,
        isAdmEnabled: featureFlags.isAdmEnabled && isAdminOrSelfServeEnabled,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  )
}

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext)
  return context
}

/**
 * @param {string | undefined} flag
 * @param {boolean} fallback
 */
function parseBoolean(flag, fallback) {
  const value = typeof flag === 'string' ? flag.toLowerCase() : flag
  if (value === 'true') return true
  if (value === 'false') return false
  return fallback
}
